import React from 'react';
import {
  Autocomplete,
  Box,
  Checkbox, FormControl, FormControlLabel, FormHelperText, FormLabel, Grid, InputLabel, ListSubheader, MenuItem, Radio, RadioGroup, Select, TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { Field } from 'react-final-form';
import validator from 'validator';
import { useQuery } from '@apollo/client';
import { titleOptions } from '../util/titleOptions';
import { getTitles } from '../gql';

const requiredValidation = (value) => (value || value === 0 ? undefined : 'Erforderlich');
const emailValidation = (value) => (!value || validator.isEmail(value) ? undefined : 'Ungültige E-Mail Adresse');
const composeValidators = (...validators) => (value) => validators.reduce((error, validator) => error || validator(value), undefined);

export default function InputField({
  name,
  label,
  disabled,
  gridSize = { xs: 12, lg: 6 },
  type = null,
  required = false,
  options = [],
  optionGroups = [],
  placeholder,
  noOptionsText,
  helperText,
  onChange,
}) {
  const { data: dynamicTitles } = useQuery(getTitles, { skip: type !== 'titleSelect' });

  // Compose validators based on the `required` prop
  const validators = [];
  if (required) validators.push(requiredValidation);
  if (type === 'email') validators.push(emailValidation);

  if (type === 'checkbox') {
    return (
      <Field
        name={name}
        type="checkbox"
        render={({ input }) => (
          <Grid item {...gridSize}>
            <FormControlLabel
              control={<Checkbox />}
              label={label}
              disabled={disabled}
              sx={{ marginTop: '7px', marginLeft: 0 }}
              {...input}
            />
          </Grid>
        )}
      />
    );
  }

  if (type === 'radio') {
    return (
      <FormControl>
        {label && <FormLabel id="demo-radio-buttons-group-label">{label}</FormLabel>}
        <RadioGroup
          aria-labelledby="demo-radio-buttons-group-label"
          name="radio-buttons-group"
        >
          {options.map((option) => (
            <FormControlLabel
              key={option.value}
              control={(
                <Field
                  name={name}
                  type="radio"
                  render={({
                    input: {
                      name, value, onChange, checked, ...restInput
                    },
                  }) => (
                    <Radio
                      name={name}
                      value={value}
                      onChange={onChange}
                      checked={checked}
                      inputProps={restInput}
                      required={required}
                      disabled={disabled}
                    />
                  )}
                />
              )}
              label={option.name}
              value={option.value}
            />
          ))}
        </RadioGroup>
      </FormControl>
    );
  }

  if (type === 'radioButton') {
    return (
      <Field name={name} validate={composeValidators(...validators)}>
        {({ input, meta }) => (
          <Grid item {...gridSize}>
            <FormControl
              error={meta.touched && (meta.error || meta.submitError)}
              fullWidth
              sx={{
                marginLeft: { xs: 0, sm: 1 },
                marginRight: { xs: 0, sm: 1 },
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: { xs: 'stretch', sm: 'center' },
                  gap: 2,
                }}
              >
                {label && (
                  <Typography
                    variant="body1"
                    color={(meta.touched && (meta.error || meta.submitError)) ? 'error' : ''}
                    sx={{
                      whiteSpace: { xs: 'normal', sm: 'nowrap' },
                      marginRight: { xs: 0, sm: 2 },
                    }}
                  >
                    {label}
                    {required && '*'}
                  </Typography>
                )}
                <ToggleButtonGroup
                  exclusive
                  value={input.value || []}
                  onChange={(event, newValue) => {
                    // Prevent deselecting in exclusive mode
                    if (newValue === null) return;
                    input.onChange(newValue);
                  }}
                  disabled={disabled}
                  color="primary"
                  sx={{
                    flexGrow: 1, // Allow buttons to take up remaining space
                    display: 'flex', // Ensure buttons are horizontally aligned
                    width: { xs: '100%', sm: 'auto' },
                  }}
                >
                  {options.map((option) => (
                    <ToggleButton
                      key={option.value}
                      value={option.value}
                      sx={{
                        flex: { xs: 1, sm: 'initial' },
                        textAlign: 'center', // Center text inside buttons
                        color: 'black',
                        '&.Mui-selected': {
                          backgroundColor: 'primary.main', // Set primary color for selected button
                          color: '#fff', // Ensure text is readable
                          '&:hover': {
                            backgroundColor: 'primary.dark', // Darker shade on hover
                          },
                        },
                      }}
                    >
                      {option.name}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Box>

              {meta.touched && meta.error && (
                <Typography variant="caption" color="error">
                  {meta.error}
                </Typography>
              )}
            </FormControl>
          </Grid>
        )}
      </Field>
    );
  }

  if (type === 'titleSelect') {
    return (
      <Field
        name={name}
        validate={composeValidators(...validators)}
        render={({ input, meta }) => (
          <Grid item {...gridSize}>
            <FormControl fullWidth required={required} error={meta.touched && (meta.error || meta.submitError)}>
              <InputLabel id="demo-simple-select-label">{label}</InputLabel>
              <Select
                label={label}
                disabled={disabled}
                MenuProps={{ disableScrollLock: true }}
                {...input}
                required={false}
              >
                {dynamicTitles?.getTitles?.length
                  ? dynamicTitles?.getTitles.map((title) => (<MenuItem value={title.code}>{title.name}</MenuItem>))
                  : titleOptions.map((option) => (<MenuItem value={option.value}>{option.name}</MenuItem>))}
              </Select>
              {(meta.error && meta.touched) || helperText ? (
                <FormHelperText>{(meta.error && meta.touched && (meta.error || meta.submitError)) || helperText}</FormHelperText>
              ) : null}
            </FormControl>
          </Grid>
        )}
      />
    );
  }

  if (type === 'select') {
    return (
      <Field
        name={name}
        validate={composeValidators(...validators)}
        render={({ input, meta }) => (
          <Grid item {...gridSize}>
            <FormControl fullWidth error={meta.touched && (meta.error || meta.submitError)}>
              <InputLabel id="demo-simple-select-label">{label}</InputLabel>
              <Select
                label={label}
                /* required={required} */
                MenuProps={{ disableScrollLock: true }}
                {...input}
                onChange={(event) => {
                  input.onChange(event);
                  if (onChange) onChange(event);
                }}
              >
                {placeholder && <MenuItem disabled value=""><em>{placeholder}</em></MenuItem>}
                {options.map((option) => (<MenuItem key={option.value} value={option.value}>{option.name}</MenuItem>))}
                {optionGroups.filter((group) => group.options.length > 0).flatMap((group) => [
                  <ListSubheader key={`${group.label}-header`}>{group.label}</ListSubheader>,
                  ...group.options.map((option) => (
                    <MenuItem key={`${group.label}-${option.value}`} value={option.value}>{option.name}</MenuItem>
                  )),
                ])}
              </Select>
              {(meta.error && meta.touched) || helperText ? (
                <FormHelperText>{(meta.error && meta.touched && (meta.error || meta.submitError)) || helperText}</FormHelperText>
              ) : null}
            </FormControl>
          </Grid>
        )}
      />
    );
  }

  if (type === 'autocomplete') {
    return (
      <Field
        name={name}
        validate={composeValidators(...validators)}
        render={({ input, meta }) => {
          let selectedOption = null;
          if (input.value !== undefined) {
            selectedOption = options.find((option) => option.value === input.value);
            if (selectedOption) selectedOption = { label: selectedOption.name, value: selectedOption.value };
          }
          return (
            <Grid item {...gridSize}>
              <Autocomplete
                required={required}
                fullWidth
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={label}
                    error={meta.touched && meta.error}
                    helperText={meta.touched && meta.error}
                  />
                )}
                options={options.map((option) => ({ label: option.name, value: option.value }))}
                {...input}
                value={selectedOption}
                isOptionEqualToValue={(option, value) => option?.value === value?.value}
                noOptionsText={noOptionsText}
                sx={{
                  '& .MuiAutocomplete-clearIndicator': {
                    visibility: 'visible !important',
                  },
                }}
                onChange={(_event, newValue) => {
                  input.onChange(newValue?.value);
                }}
              />
            </Grid>
          );
        }}
      />
    );
  }

  return (
    <Field
      name={name}
      validate={composeValidators(...validators)}
      render={({ input, meta }) => (
        <Grid item {...gridSize}>
          <TextField
            error={meta.touched && !meta.dirtySinceLastSubmit && (meta.error || meta.submitError)}
            type={type}
            label={`${label} ${required ? '*' : ''}`}
            variant="outlined"
            fullWidth
            disabled={disabled}
            placeholder={placeholder}
            helperText={(meta.touched && !meta.dirtySinceLastSubmit && (meta.error || meta.submitError)) || helperText}
            {...input}
          />
        </Grid>
      )}
    />
  );
}
